import type { GuideStep } from "../../content/guided-help";

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function guideContext(pathname: string, search: string) {
  const params = new URLSearchParams(search);
  const parts = pathname.split("/");
  const valid = (value: string | null | undefined) =>
    value && uuid.test(value) ? value : undefined;
  return {
    companyId: valid(
      parts[1] === "companies" ? parts[2] : pathname === "/help" ? params.get("company") : null,
    ),
    projectId: valid(
      parts[1] === "projects" ? parts[2] : pathname === "/help" ? params.get("project") : null,
    ),
    requestId: valid(parts[1] === "quote-requests" ? parts[2] : params.get("request")),
  };
}
export function guideHref(projectId?: string, requestId?: string, companyId?: string) {
  const params = new URLSearchParams();
  if (companyId) params.set("company", companyId);
  if (projectId) params.set("project", projectId);
  if (requestId) params.set("request", requestId);
  return `/help${params.size ? `?${params}` : ""}`;
}
/** Navigation only: every destination still enforces its own permissions and unsaved guard. */
export function guideDestination(
  step: GuideStep,
  projectId: string,
  requestId?: string,
  companyId?: string,
) {
  if (step === "client") return companyId ? `/companies/${companyId}` : "/companies";
  const base = `/projects/${projectId}?view=preparation`;
  if (!requestId) return base;
  if (step === "followup") return `/projects/${projectId}?view=insurers`;
  return `${base}&request=${encodeURIComponent(requestId)}&request_section=${step}`;
}

export function rememberGuide(
  userId: string,
  tenantId: string,
  projectId: string,
  requestId?: string,
) {
  try {
    sessionStorage.setItem(
      `kv-guide-context-v1:${userId}:${tenantId}`,
      JSON.stringify({ projectId, requestId }),
    );
  } catch {
    /* Optional reading position; authorization never relies on storage. */
  }
}
export function resumeGuideHref(userId?: string, tenantId?: string | null) {
  if (!userId || !tenantId) return "/help";
  try {
    const value = JSON.parse(
      sessionStorage.getItem(`kv-guide-context-v1:${userId}:${tenantId}`) ?? "null",
    );
    if (!value || !uuid.test(value.projectId) || (value.requestId && !uuid.test(value.requestId)))
      return "/help";
    return guideHref(value.projectId, value.requestId);
  } catch {
    return "/help";
  }
}

/** Return context is local navigation state, scoped to the same person and firm. */
export function guideReturnPath(state: unknown, userId?: string, tenantId?: string | null) {
  if (!state || typeof state !== "object" || !userId || !tenantId) return undefined;
  const value = state as Record<string, unknown>;
  if (
    value.userId !== userId ||
    value.tenantId !== tenantId ||
    typeof value.returnTo !== "string" ||
    !value.returnTo.startsWith("/")
  )
    return undefined;
  try {
    const url = new URL(value.returnTo, "https://guide.invalid");
    if (url.origin !== "https://guide.invalid" || url.pathname === "/help") return undefined;
    return url.pathname + url.search + url.hash;
  } catch {
    return undefined;
  }
}

/** The new draft has no existing project/request context yet. */
export function isStartRequestPath(pathname: string) {
  return /^\/requests\/new(?:\/start)?\/?$/.test(pathname);
}
